/**
 * ISO-day normalisation, shared by every part of the AI filter pipeline that
 * turns a date a model or an artifact carries into a bound something can read.
 *
 * ONE definition, because the three copies that existed before this module
 * did not agree. `date-range.resolver.ts` validated the calendar day;
 * `read-scope.ts` matched a `YYYY-MM-DD` prefix without validating it, so
 * `2024-02-31` passed through into a request; and
 * `filter-artifact-to-composition-req.ts` had its own month-to-last-day
 * expansion whose doc comment claimed the uniqueness this module now actually
 * provides. A date that is rejected in one place and accepted in another is
 * the worst of the three outcomes: the read runs, and only some of the
 * pipeline knows the window is nonsense.
 */

/**
 * `'2025-08'` → `'2025-08-31'`. Day 0 of the next month is the last day of
 * this one, which is why no month-length table appears anywhere.
 */
export function lastDayOfMonth(yearMonth: string): string {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(5, 7));
  // `Date.UTC` rather than local construction: these are pure calendar
  // instances, and reading a UTC field back off a locally-built one is how the
  // sibling `daysInMonth` in date-range-presets came to say so explicitly.
  const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${yearMonth}-${String(day).padStart(2, '0')}`;
}

/**
 * Normalize a date to a full `YYYY-MM-DD`, or `null` if it isn't a real one.
 *
 * `YYYY-MM-DD` is VALIDATED: an impossible day (`2024-02-30`) is rejected
 * rather than rolled forward into March, because `new Date` rolls silently and
 * a read that quietly moved to a different month is worse than one that
 * refused. A bare `YYYY-MM` expands to the first of the month for a START
 * bound and its LAST day for an END bound, so a month-grained window covers
 * the whole month rather than a single day of it.
 *
 * Leading slashes and whitespace are stripped first: model output arrives that
 * way often enough that rejecting it would refuse windows the user really did
 * ask for.
 */
export function toIsoDay(value: string, bound: 'start' | 'end'): string | null {
  const cleaned = value.replace(/^[/\s]+/, '').trim();

  const ymd = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    const probe = new Date(year, month - 1, day);
    return probe.getMonth() === month - 1 && probe.getDate() === day
      ? cleaned
      : null;
  }

  const ym = cleaned.match(/^(\d{4})-(\d{2})$/);
  if (ym) {
    const month = Number(ym[2]);
    if (month < 1 || month > 12) return null;
    return bound === 'end' ? lastDayOfMonth(cleaned) : `${cleaned}-01`;
  }

  return null;
}

/** Shared model-facing label for validated date bounds, including open windows. */
export function formatIsoDayRange(start: unknown, end: unknown): string | null {
  const startDay = typeof start === 'string' ? toIsoDay(start, 'start') : null;
  const endDay = typeof end === 'string' ? toIsoDay(end, 'end') : null;
  if (startDay && endDay) {
    return startDay <= endDay ? `${startDay} to ${endDay}` : null;
  }
  if (startDay) return `since ${startDay}`;
  return endDay ? `through ${endDay}` : null;
}
